import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Provider, connect } from 'react-redux'
import Base from '@cx/couturier/Base'
import { Container } from '@cx/couturier/labs/ImmersiveFlow'
import InlineNotification from '@cx/couturier/InlineNotification.js'
import { Row, Col } from '@cx/couturier/Grid'
import styled from 'styled-components'
import { CompanyLogoModified } from 'cxid-common-components/util-components'
import AndroidBanner from 'cxid-common-components/android-banner'
import _map from 'lodash/map'
import config from '../utils/config.js'
import useHeapTracker from '../hooks/use-heap-tracker.js'
import {
  ANDROID_BANNER_CLOSE_TRACKING,
  ANDROID_BANNER_GET_TRACKING,
  ANDROID_BANNER_SHOW_TRACKING,
  ANDROID_BANNER_EVENT_PROPERTIES
} from '../consts/tracking.js'
import Greeting from './greeting.js'
import Username from './username.js'
import Password from './password.js'
import ErrorBanner from './error-banner.js'
import Submit from './submit.js'
import LockedOutModal from './modals/locked-modal.js'
import WelcomeBackModal from './modals/deactivated-user-modal.js'
import OutOfDateModal from './modals/browser-out-dated-modal.js'
import IntuneBrowserModal from './modals/browser-intune-modal.js'
import { AppDownloadBanner } from './app-download-banner.js'
import { HelperFont, FormattedForm, ShowForMediumUp, ShowForSmallOnly, HeaderWithLine, IFBodyNoPadding } from './component-styles.js'
import useFeatureFlag from '../hooks/use-feature-flag.js'
import useSystemMessage from '../hooks/use-system-message.js'

const HiddenIframe = styled.iframe`
  display: none;
`

const LogoutFrames = (logoutUrls) => {
  const { urls: lUrls, nmisLogoutUrl } = logoutUrls
  const randomizeUrl = `?${Math.floor(Math.random() * 100_000_000)}${Math.floor(Math.random() * 100_000_000)}`
  const urls = _map(lUrls, (url) => url + randomizeUrl)

  return (
    <div className='iframe-container'>
      {urls.map((url) => (
        <HiddenIframe key={url} src={`${url}`} title={url} />
      ))}
      <HiddenIframe key={nmisLogoutUrl} src={`${nmisLogoutUrl}`} title={nmisLogoutUrl} />
    </div>
  )
}

const SystemMessage = styled(InlineNotification)`
  margin-bottom: 3rem;
`

const SystemMessageWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: -1rem 1rem;
`

const AdditionalLinks = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`
AdditionalLinks.LinkItem = styled.li`
`

AdditionalLinks.Link = styled.a`
  white-space: nowrap;
  line-height: 2rem;
`
AdditionalLinks.Subtitle = styled.span`
  display: block;
  margin: 3rem 0 .75em 0; // 3rem to give a gap between this and the login form.
  padding: 0 0 .25em 0;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral.silverDollar};
`


const App = ({ telephone, hours, clearSession, urls, isLunaLiteEnabled, intune, badBrowser, shouldShowAndroidBanner, finalRedirectUrl, maintenanceMessages }) => {
  const trackAction = useHeapTracker()
  const [showAndroidBanner, setShowAndroidBanner] = useState(shouldShowAndroidBanner)
  const [stylesLoaded, setStylesLoaded] = useState(false)
  const CXTransmitRegistrationClassic = useFeatureFlag('CXTransmitRegistrationClassic')
  const { maintenanceMessage, systemMessageType, isSystemMessageVisible, shouldSystemMessageBeDismissable, setHasSystemMessageBeenHidden } = useSystemMessage(maintenanceMessages)
  const usernameUrl = config?.client?.recoverUsername
  const passwordUrl = config?.client?.recoverPassword


  useEffect(() => {
    // Don't render the android banner until everything is ready to avoid the flash of unstyled content
    document.addEventListener('DOMContentLoaded', setStylesLoaded(true)) // eslint-disable-line no-undef

    return () => {
      document.removeEventListener('DOMContentLoaded', setStylesLoaded(true)) // eslint-disable-line no-undef
    }
  }, [])

  // clear out query parameters from the URL to avoid retaining sensitive information in the browser history
  useEffect(() => {
    const url = new URL(window.location.href)
    url.search = ''
    window.history.replaceState(window.history.state, '',
      `${url.pathname}${url.search}`)
  }, [])


  return (
    <Base luna={isLunaLiteEnabled}>
      <LockedOutModal />
      <WelcomeBackModal />
      {stylesLoaded && (
        <AndroidBanner
          currentPage={finalRedirectUrl}
          onClose={() => trackAction(ANDROID_BANNER_CLOSE_TRACKING, ANDROID_BANNER_EVENT_PROPERTIES)}
          onGet={() => trackAction(ANDROID_BANNER_GET_TRACKING, ANDROID_BANNER_EVENT_PROPERTIES)}
          onShow={() => trackAction(ANDROID_BANNER_SHOW_TRACKING, ANDROID_BANNER_EVENT_PROPERTIES)}
          setShowAndroidBanner={setShowAndroidBanner}
          showAndroidBanner={showAndroidBanner}
        />
      )}


      {badBrowser ? <OutOfDateModal /> : null}
      {intune ? <IntuneBrowserModal /> : null}

      <noscript>
        <OutOfDateModal />
      </noscript>
      <Container className='external-footer-layout'>
        <HeaderWithLine
          PrefixComponent={
            <>
              <ShowForMediumUp alt='Northwestern Mutual Logo' aria-label='Northwestern Mutual Logo' role='img'>
                <CompanyLogoModified full />
              </ShowForMediumUp>
              <ShowForSmallOnly alt='Northwestern Mutual Logo' aria-label='Northwestern Mutual Logo' role='img'>
                <CompanyLogoModified />
              </ShowForSmallOnly>
            </>
          }
        />
        <IFBodyNoPadding>
          {isSystemMessageVisible && (
            <SystemMessageWrapper>
              <SystemMessage type={systemMessageType} isOpen={isSystemMessageVisible} onClose={shouldSystemMessageBeDismissable ? () => setHasSystemMessageBeenHidden(true) : null} >
                <p data-testid="system-message-text">{maintenanceMessage.text}</p>
              </SystemMessage>
            </SystemMessageWrapper>
          )}
          <FormattedForm autoComplete='off' method='POST'>
            <Row>
              <Col medium={12} small={12}>
                <Greeting />
                <ErrorBanner />
                <Username />
                <Password />
                <HelperFont className='forgot' marginBottom='40px'>
                  Forgot your{' '}
                  {usernameUrl && (
                    <a alt='Forgot Username' href={usernameUrl} id='forgot-username'>
                      username
                    </a>
                  )}{' '}
                  or{' '}
                  {passwordUrl && (
                    <a alt='Forgot Password' href={passwordUrl} id='forgot-password'>
                      password
                    </a>
                  )}
                  ?
                </HelperFont>

                <Submit />

                {/* Additional Links */}
                <AdditionalLinks.Subtitle>
                  Log in to other Northwestern Mutual sites
                </AdditionalLinks.Subtitle>
                <AdditionalLinks>
                  <AdditionalLinks.LinkItem>
                    <AdditionalLinks.Link aria-label="Visit Northwestern Access Fund." href="https://www.eaccountservices.com/NM/" target="_blank" rel="nofollow noopener noreferrer">
                      Access Fund
                    </AdditionalLinks.Link>
                  </AdditionalLinks.LinkItem>
                  <AdditionalLinks.LinkItem>
                    <AdditionalLinks.Link aria-label="Visit Northwestern Mutual Business Insurance." href="https://bcis.northwesternmutual.com/bcis/update/login/" target="_blank" rel="nofollow noopener noreferrer">
                      Business Insurance
                    </AdditionalLinks.Link>
                  </AdditionalLinks.LinkItem>
                </AdditionalLinks>

                <HelperFont className='sign-up' marginTop='40px'>
                  Don’t have an online account?{' '}
                  <a alt='Sign up' href={`${CXTransmitRegistrationClassic ? '/auth/registration/classic' : '/registration'}` } id='register'>
                    Register
                  </a>
                </HelperFont>
              </Col>
            </Row>
            <AppDownloadBanner />
          </FormattedForm>
        </IFBodyNoPadding>
        {clearSession ? <LogoutFrames {...urls} /> : null}
      </Container>
    </Base>
  )
}

App.propTypes = {
  telephone: PropTypes.string,
  hours: PropTypes.string,
  clearSession: PropTypes.bool,
  urls: PropTypes.arrayOf(PropTypes.string),
  isLunaLiteEnabled: PropTypes.bool,
  intune: PropTypes.bool,
  badBrowser: PropTypes.bool,
  shouldShowAndroidBanner: PropTypes.bool,
  finalRedirectUrl: PropTypes.string,
  maintenanceMessages: PropTypes.array
}

const mapStateToProps = (state) => ({
  telephone: state.meta.supportHours.telephone,
  badBrowser: state.submit.badBrowser,
  intune: state.submit.isIntuneManagedBrowser,
  clearSession: state.meta.clearSession,
  hours: state.meta.supportHours.hours,
  urls: state.meta.logoutUrls,
  isLunaLiteEnabled: state.meta.isLunaLiteEnabled,
  finalRedirectUrl: state.meta.finalRedirectUrl,
  shouldShowAndroidBanner: state.meta.mobileWebViewState.shouldShowAndroidBanner,
  maintenanceMessages: state.maintenanceMessages
})
const ConnectedApp = connect(mapStateToProps)(App)

const ProviderComponent = ({ store }) => (
  <Provider store={store}>
    <ConnectedApp />
  </Provider>
)

ProviderComponent.displayName = 'ProviderComponent'

export default ProviderComponent
